import { GooglePlaceScript, useGooglePlace } from '@google-place';
import useTranslation from 'next-translate/useTranslation';
import { ReactNode, useCallback, useRef, useState } from 'react';
import {
  Control,
  ControllerFieldState,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';

import { Translate } from '@core/constant';
import { useContextMediaQuery } from '@core/context';
import { AddressForm, EColor, ECountry, EIcon } from '@core/type';

import { FormControl, InputTextControl } from '../form';
import { InputText, InputWrapper } from '../input';
import { Box } from '../layout';
import { Link } from '../link';
import { AddressAutocompleteGlobalStyle } from './AddressAutocompleteGlobalStyle';
import { CITY, COUNTRY, IPlace, POSTCODE, STATE, STREET, STREET_NUMBER } from './interface';
import { isPlaceOutsideCountryList } from './utils';

export const ADDRESS_FORM = 'AddressForm';

export const AddressAutocomplete = ({
  countryCode,
  onSubmit,
  children,
  handleSubmit,
  control,
  trigger,
  reset,
  setError,
  setValue,
  onError,
  onSuccess,
  onInvalid,
  clearCountry = true,
  childrenBefore,
  watch,
  nonce,
  googleApiKey,
}: {
  countryCode?: ECountry | ECountry[];
  onSubmit: (data: AddressForm) => unknown;
  children?: ReactNode;
  handleSubmit: UseFormHandleSubmit<AddressForm>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<AddressForm, any>;
  trigger: UseFormTrigger<AddressForm>;
  reset: UseFormReset<AddressForm>;
  setError: UseFormSetError<AddressForm>;
  setValue: UseFormSetValue<AddressForm>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (data: any) => Promise<void> | void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: any) => Promise<void> | void;
  onInvalid?: () => void;
  clearCountry?: boolean;
  childrenBefore?: ReactNode;
  watch: UseFormWatch<AddressForm>;
  nonce: string;
  googleApiKey: string;
}) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef<HTMLInputElement>();
  const { t: tForm } = useTranslation(Translate.common.FORM);
  const { t: tButton } = useTranslation(Translate.common.BUTTON);
  const { isMobile } = useContextMediaQuery();
  const [isLocked, setIsLocked] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  const clearFields = useCallback(
    (resetForm = true) => {
      setValue('streetName', '');
      setValue('streetNumber', '');
      setValue('zipCode', '');
      setValue('additionalInfo', '');
      setValue('city', '');
      setValue('provinceIso', '');
      setValue('streetName', '');
      if (clearCountry) {
        setValue('countryIso', null);
        setValue('country', '');
      }
      if (resetForm) {
        setIsLocked(true);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setErrorMessage('');
      }
    },
    [clearCountry, setValue],
  );

  const handleClickPlace = useCallback(
    (place: IPlace) => {
      setIsLocked(false);
      clearFields(false);

      if (Array.isArray(countryCode)) {
        if (isPlaceOutsideCountryList(place, countryCode)) {
          setErrorMessage(tForm('undeliverableCountry'));
          return;
        }
      }

      if (place?.address_components) {
        for (const component of place.address_components) {
          const componentType = component.types[0];

          switch (componentType) {
            case STREET_NUMBER:
              setValue('streetNumber', component.long_name);
              break;
            case STREET:
              setValue('streetName', component.short_name);
              break;
            case POSTCODE:
              setValue('zipCode', component.long_name);
              break;
            case CITY:
              setValue('city', component.long_name);
              break;
            case COUNTRY:
              setValue('country', component.long_name);
              setValue('countryIso', component.short_name as ECountry);
              break;
            case STATE: {
              setValue('provinceIso', component.short_name);
              break;
            }
            default:
              break;
          }
        }
      } else {
        setErrorMessage(tForm('emptyResultAddress'));
      }

      void trigger();
    },
    [trigger, setValue, countryCode],
  );

  useGooglePlace(
    inputRef,
    autoCompleteRef,
    Array.isArray(countryCode) ? undefined : countryCode,
    handleClickPlace,
  );

  return (
    <>
      {childrenBefore}
      {nonce && <GooglePlaceScript nonce={nonce} googleApiKey={googleApiKey} />}
      <AddressAutocompleteGlobalStyle />

      <Box marginBottom={'32px'} display="flex" flexDirection="column" gap="16px">
        <InputWrapper>
          <InputText
            placeholder={tForm('address.placeholder')}
            ref={inputRef}
            fieldState={
              errorMessage
                ? ({
                    error: { message: errorMessage },
                    isTouched: true,
                  } as ControllerFieldState)
                : null
            }
            onChange={() => {
              if (inputRef.current.value) {
                setErrorMessage('');
              }
            }}
          />
        </InputWrapper>
        <Box display="flex" justifyContent="flex-end">
          <Link color={EColor.SECONDARY} onClick={() => clearFields()} endIcon={EIcon.CIRCLE_CROSS}>
            {tButton('clearFields.title')}
          </Link>
        </Box>
      </Box>
      <FormControl
        t={tForm}
        trigger={trigger}
        reset={reset}
        handleSubmit={handleSubmit}
        setError={setError}
        id={ADDRESS_FORM}
        onInvalid={() => {
          if (!inputRef.current.value && !addressCompleted(watch)) {
            setErrorMessage(tForm('address.validate.required'));
          }
          onInvalid?.();
        }}
        fn={onSubmit}
        onError={onError}
        onSuccess={onSuccess}
      >
        <Box display="flex" gap="16px" flexDirection="column">
          <Box
            display="flex"
            gap={isMobile ? '16px' : '8px'}
            flexDirection={isMobile ? 'column' : 'row'}
          >
            <Box width={isMobile ? '100%' : '66%'}>
              <InputTextControl
                control={control}
                disabled={isLocked}
                required
                name="streetName"
                placeholder={tForm('streetName.placeholder')}
              />
            </Box>
            <Box width={isMobile ? '100%' : '34%'}>
              <InputTextControl
                control={control}
                disabled={isLocked}
                required
                name="streetNumber"
                placeholder={tForm('streetNumber.placeholder')}
              />
            </Box>
          </Box>
          <InputWrapper>
            <InputTextControl
              control={control}
              disabled={isLocked}
              name="additionalInfo"
              placeholder={tForm('additionalInfo.placeholder')}
            />
          </InputWrapper>
          <Box
            display="flex"
            gap={isMobile ? '16px' : '8px'}
            flexDirection={isMobile ? 'column' : 'row'}
          >
            <InputWrapper width="100%">
              <InputTextControl
                control={control}
                required
                disabled={isLocked}
                name="zipCode"
                placeholder={tForm('zipCode.placeholder')}
              />
            </InputWrapper>
            <InputWrapper width="100%">
              <InputTextControl
                control={control}
                disabled={isLocked}
                required
                name="city"
                placeholder={tForm('city.placeholder')}
              />
            </InputWrapper>
          </Box>
          <InputWrapper>
            <InputTextControl
              control={control}
              disabled
              required
              name="country"
              placeholder={tForm('country.placeholder')}
            />
          </InputWrapper>
        </Box>
        {children}
      </FormControl>
    </>
  );
};

const addressCompleted = (watch: UseFormWatch<AddressForm>) =>
  watch('city') &&
  watch('countryIso') &&
  watch('streetName') &&
  watch('streetNumber') &&
  watch('zipCode');
